<template>
    <pc-loader
        data-test-role="table-loader"
        v-bind:loading="loading">
        <div
            data-test-role="table-container"
            v-bind:data-test-id="internalTestId">
            <div
                v-if="!hideSearch || showContextMenuSwitch"
                class="row align-items-center justify-content-end">
                <pc-checkbox
                    v-if="showContextMenuSwitch"
                    v-model="enableContextMenu"
                    class="col ms-3"
                    style="color: var(--pc-cp-admin-color)"
                    switch
                    warning
                    label="Context menu" />
                <div
                    v-if="!hideSearch"
                    v-bind:class="searchContainerClasses">
                    <pc-search
                        v-bind:dynamic="{ reactive: false }"
                        v-bind:initial-search="searchTerm"
                        v-bind:maxlength="searchTermLength"
                        v-bind:filter-options="filterOptions"
                        v-bind:initial-filters="searchFilters"
                        v-bind:no-filter-text="noFilterText"
                        v-on:search="search"
                        v-on:search-filters="searchWithFilters" />
                </div>
            </div>
            <template v-if="currentPageItems.length > 0 || (displayTableWhenEmpty && useTableView)">
                <div
                    v-if="showItemCount"
                    class="d-flex justify-content-end mb-2">
                    {{ itemCountDisplay }}
                </div>
                <table-view
                    v-if="useTableView"
                    ref="tableView"
                    v-bind:items="currentPageItems"
                    v-bind:columns="internalColumns"
                    v-bind:key-prop="keyProp"
                    v-bind:show-actions="showActions"
                    v-bind:show-footer="showFooter"
                    v-bind:show-chevron="showChevronPerItem"
                    v-bind:use-global-scroll="useGlobalScroll"
                    v-bind:show-kebab="enableContextMenu"
                    data-test-role="table-wide-view"
                    v-on:sort-click="sortClick"
                    v-on:row-click="rowClick"
                    v-on:help-click="helpClick"
                    v-on:cell-click="cellClick">
                    <template
                        v-for="column in internalColumns"
                        v-slot:[column.property]="props">
                        <slot
                            v-bind:name="column.property"
                            v-bind="props">
                            {{ displayValue(column, props) }}
                        </slot>
                    </template>
                    <template v-slot:actionsRow="props">
                        <slot
                            v-bind="props"
                            name="actionsRow" />
                    </template>
                    <template v-slot:contextMenu="props">
                        <context-menu
                            v-bind:row-object="props"
                            v-bind:menu-options="contextMenuActions" />
                    </template>
                    <template v-slot:emptyContent>
                        <slot name="emptyContent">
                            <em>{{ emptyTextLoc }}</em>
                        </slot>
                    </template>
                    <template v-slot:footer>
                        <slot name="footer" />
                    </template>
                </table-view>
                <card-view
                    v-else
                    v-bind:items="currentPageItems"
                    v-bind:columns="internalColumns"
                    v-bind:key-prop="keyProp"
                    v-bind:show-actions="showActions"
                    v-bind:hide-column-names="cardHideHeaders"
                    v-bind:property-cutoff="cardPropertyCutoff"
                    v-bind:hide-sort="cardHideSort"
                    v-bind:primary-sort-index="primarySortIndex"
                    v-bind:hide-horizontal-separator="hideCardHorizontalSeparator"
                    v-bind:show-kebab="enableContextMenu"
                    data-test-role="table-card-view"
                    v-on:sort-click="sortClick"
                    v-on:row-click="rowClick"
                    v-on:help-click="helpClick"
                    v-on:cell-click="cellClick">
                    <template
                        v-for="column in internalColumns"
                        v-slot:[column.property]="props">
                        <div
                            v-if="cardShowEmptyPlaceholder && _isDisplayValueEmpty(column, props)"
                            v-bind:key="`empty-placeholder-${column.property}-${props[keyProp]}`"
                            class="text-body-secondary fst-italic">
                            None
                        </div>
                        <slot
                            v-else
                            v-bind:name="column.property"
                            v-bind="props">
                            {{ displayValue(column, props) }}
                        </slot>
                    </template>
                    <template v-slot:actionsRow="props">
                        <slot
                            v-bind="props"
                            name="actionsRow" />
                    </template>
                    <template v-slot:contextMenu="props">
                        <context-menu
                            v-bind:row-object="props"
                            v-bind:menu-options="contextMenuActions" />
                    </template>
                </card-view>
                <div
                    v-if="showLastPageMessage"
                    class="d-flex justify-content-center fst-italic">
                    {{ lastPageMessage }}
                </div>
            </template>
            <template v-else>
                <div v-bind:data-test-role="emptyTableTestRole">
                    <slot name="emptyContent">
                        <em>{{ emptyTextLoc }}</em>
                    </slot>
                </div>
            </template>
            <div class="row g-0">
                <items-per-page-selector
                    v-if="internalShowItemsPerPageSelector"
                    v-model="internalItemsPerPage"
                    class="col-12 col-md" />
                <pagination
                    v-if="showPagination"
                    class="col-12 col-md"
                    v-bind:current-page="currentPage"
                    v-bind:number-of-pages="numberOfPages"
                    v-on:page-changed="pageChanged" />
            </div>
        </div>
    </pc-loader>
</template>

<script>
    import _table from "./_table.vue";
    import _card from "./_card.vue";
    import _itemsPerPageSelector, { MIN_ITEMS_PER_PAGE } from "./_itemsPerPageSelector.vue";
    import _pagination from "./_pagination.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./pc-table.i18n";

    import { BP } from "Responsive/breakpoint";
    import { isAtLeast } from "Responsive/manager";
    import { escapeRegex, safeSetItem, deepClone, scrollToElement } from "Util/util";
    import { FILTER_PROPS } from "../search/searchFilter.vue";
    import contextMenu from "Components/table/_contextMenu.vue";

    // The breakpoint at which the table switches from card to table view
    export const TABLE_CARD_BP = BP.sm;

    export default {
        components: {
            "table-view": _table,
            "card-view": _card,
            itemsPerPageSelector: _itemsPerPageSelector,
            pagination: _pagination,
            contextMenu
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            items: { type: [Array, Function], required: true },
            columns: { type: Array, required: true },

            // Name of a property or properties whose value (or a tuple of their values)
            // will be unique for every item in the table.
            // If no such property exists or the empty string is specified, the entire item
            // will be JSONified as a unique id.
            keyProp: { type: [String, Array], required: true },

            initialSort: {
                type: Array,
                required: false,
                default: () => [[0, "asc"]]
            },
            itemsPerPage: { type: Number, required: false, default: 15 },
            showItemsPerPageSelector: { type: Boolean, required: false, default: false },
            maxPages: { type: Number, required: false, default: -1 },
            lastPageMessage: { type: String, required: false, default: null },
            showItemCount: { type: Boolean, required: false, default: false },
            maxItemCount: { type: Number, required: false, default: -1 },
            itemCountText: { type: String, required: false, default: "Results" },
            hideSearch: { type: Boolean, required: false, default: false },
            emptyText: { type: String, required: false, default: null },
            cardHideHeaders: { type: Boolean, required: false, default: false },
            cardHideSort: { type: Boolean, required: false, default: false },
            cardPropertyCutoff: { type: Number, required: false, default: 3 },
            // Show a placeholder value in card mode if the cell value is empty as
            // determined by column.isValueEmpty
            cardShowEmptyPlaceholder: { type: Boolean, required: false, default: false },

            // Useful in cases where actionsRow slot is defined but we conditionally
            // may not want to show it.
            hideActions: { type: Boolean, required: false, default: false },
            showActionsPerItem: { type: Function, required: false, default: () => true },
            saveState: { type: Boolean, required: false, default: false },
            stateKey: { type: String, required: false, default: null },
            useGlobalScroll: { type: Boolean, required: false, default: false },
            // Override pagination reset behavior when items change
            preservePage: { type: Boolean, required: false, default: false },
            // Display table headers even if items is empty
            displayTableWhenEmpty: { type: Boolean, required: false, default: false },
            searchTermLength: { type: Number, required: false, default: null },
            // Filtering for project list search
            filterOptions: { type: Array, required: false, default: () => [] },
            noFilterText: { type: String, required: false, default: null },

            noCardView: { type: Boolean, required: false, default: false },
            expandedOverride: { type: Function, required: false, default: null },
            showChevronPerItem: { type: Function, required: false, default: () => true },
            hideCardHorizontalSeparator: { type: Boolean, required: false, default: false },
            contextMenuActions: { type: Object, required: false, default: () => {} }
        },
        emits: ["help-clicked", "row-clicked", "row-expanded", "row-collapsed", "cell-click", "page-changed"],
        data: function () {
            const internalTestId = this.$idGenerator.getNextId("pcTableTestId");

            const internalColumns = this._buildInternalColumns();
            const data = {
                currentPage: 1,
                currentPageItems: [],
                totalItemCount: 0,
                filteredItemCount: 0,
                loading: false,
                searchTerm: "",
                searchFilters: [],
                colIndexSortOrder: internalColumns.indexSortOrder,
                internalColumns: internalColumns.cols,
                hasActions: false,
                hasFooter: false,
                filterToFind: null,
                filterFallbackPage: null,
                internalTestId,
                itemsPerPageOverride: this.itemsPerPage,
                allResultsIndexArray: [],
                enableContextMenu: false
            };
            if (this.showItemsPerPageSelector) {
                const savedItemsPerPage = localStorage.getItem(this._getItemsPerPageLocalStorageKey());
                if (savedItemsPerPage) {
                    data.itemsPerPageOverride = parseInt(savedItemsPerPage);
                }
            }
            if (this.saveState && this.stateKey) {
                let savedState = localStorage.getItem(this._getLocalStorageKey());
                savedState = JSON.parse(savedState);
                const internalItemsPerPage = this.showItemsPerPageSelector
                    ? data.itemsPerPageOverride
                    : this.itemsPerPage;
                if (
                    savedState &&
                    internalItemsPerPage === savedState.itemsPerPage &&
                    data.internalColumns.length === savedState.internalColumnsDirs.length
                ) {
                    data.currentPage = savedState.currentPage;
                    data.searchTerm = savedState.searchTerm;
                    if (savedState.searchFilters) {
                        data.searchFilters = savedState.searchFilters.filter((f) =>
                            this.filterOptions.some(
                                (fo) =>
                                    f[FILTER_PROPS.idProp] === fo[FILTER_PROPS.idProp] &&
                                    f[FILTER_PROPS.textProp] === fo[FILTER_PROPS.textProp] &&
                                    f[FILTER_PROPS.groupIdProp] === fo[FILTER_PROPS.groupIdProp]
                            )
                        );
                    }
                    data.colIndexSortOrder = savedState.colIndexSortOrder;
                    data.internalColumns.forEach((col, i) => (col.sortingDir = savedState.internalColumnsDirs[i]));
                }
            }
            return data;
        },
        computed: {
            emptyTextLoc() {
                return this.emptyText ?? this.emptyTextDefault;
            },
            internalItemsPerPage: {
                get() {
                    return this.showItemsPerPageSelector ? this.itemsPerPageOverride : this.itemsPerPage;
                },
                set(value) {
                    this.currentPage = 1;
                    this.itemsPerPageOverride = value;
                    safeSetItem(this._getItemsPerPageLocalStorageKey(), this.itemsPerPageOverride);
                }
            },
            numberOfPages() {
                let numPages = Math.max(Math.ceil(this.filteredItemCount / this.internalItemsPerPage), 1);
                if (this.maxPages > 0) {
                    numPages = Math.min(numPages, this.maxPages);
                }
                return numPages;
            },
            showLastPageMessage() {
                return this.maxPages > 0 && this.lastPageMessage && this.currentPage == this.maxPages;
            },
            query() {
                return {
                    start: (this.currentPage - 1) * this.internalItemsPerPage,
                    length: this.internalItemsPerPage,
                    search: {
                        value: this.searchTerm,
                        regex: false
                    },
                    order: this.order,
                    columns: this.columns,
                    filters: this.searchFilters
                };
            },
            order() {
                let colOrder = [];
                if (this.internalColumns.length > 0) {
                    colOrder = this.colIndexSortOrder.map((i) => ({
                        column: i,
                        dir: this.internalColumns[i].sortingDir
                    }));
                }
                return colOrder;
            },
            primarySortIndex() {
                if (this.colIndexSortOrder.length > 0) {
                    return this.colIndexSortOrder[0];
                }
                return 0;
            },
            internalShowItemsPerPageSelector() {
                return this.showItemsPerPageSelector && this.totalItemCount > MIN_ITEMS_PER_PAGE;
            },
            showPagination() {
                return this.totalItemCount > this.internalItemsPerPage;
            },
            itemCountValue() {
                if (this.maxItemCount > 0 && this.totalItemCount >= this.maxItemCount) {
                    return this.maxItemCount + "+";
                }
                return this.totalItemCount.toString();
            },
            itemCountDisplay() {
                if (!this.showItemCount) {
                    return "";
                }
                return `${this.itemCountValue} ${this.itemCountText}`;
            },
            useTableView() {
                return isAtLeast(this.viewport, TABLE_CARD_BP) || this.noCardView;
            },
            showActions() {
                if (this.hideActions || !this.hasActions) {
                    return () => false;
                }
                return this.showActionsPerItem;
            },
            showFooter() {
                if (this.hasFooter) {
                    return () => true;
                }
                return () => false;
            },
            visibleColumns() {
                return this.columns.filter((c) => c.visible);
            },
            emptyTableTestRole() {
                return `${this.internalTestId}-empty-table`;
            },
            useFilters() {
                return this.filterOptions.length > 0;
            },
            searchContainerClasses() {
                return {
                    "my-3": true,
                    "col-md-8 col-lg-6": this.useFilters,
                    "col-md-6 col-lg-4": !this.useFilters
                };
            },
            showContextMenuSwitch() {
                return !!localStorage.getItem("pcTableContextMenuSwitchEnable") && !!this.contextMenuActions;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            // deep watch is used here to trigger on changes in query's nested properties
            // These changes which would be missed by the shallow watch made default by vue3
            query: {
                handler() {
                    this._performQuery();
                },
                deep: true
            },
            columns() {
                const cols = this._buildInternalColumns();
                this.internalColumns = cols.cols;
                this.colIndexSortOrder = cols.indexSortOrder;
            },
            // Reload items only when the entire array is replaced or the length of the items changes
            // With just a deep watch, a change to any element of the array triggers an items reload
            items(oldValue, newValue) {
                if (newValue !== oldValue) {
                    this.reloadItems();
                }
            },
            "items.length"() {
                if (typeof this.items !== "function") {
                    this.reloadItems();
                }
            },
            numberOfPages() {
                // Prevent a scenario where the number of pages is reduced
                // by some external mechanism such that the current page
                // recovered from the cache no longer exists.
                if (this.currentPage > this.numberOfPages) {
                    this.currentPage = this.numberOfPages;
                }
            }
        },
        mounted() {
            // $slots is not reactive and is only guaranteed to be
            // fully initialized once the component has been mounted
            this.hasActions = Boolean(this.$slots.actionsRow);
            this.hasFooter = Boolean(this.$slots.footer);
        },
        created() {
            this._performQuery();
        },
        methods: {
            displayValue(column, props) {
                let val = props[column.property];
                if (typeof column.displayValue === "function") {
                    val = column.displayValue(val, props);
                }
                return val;
            },
            _isDisplayValueEmpty(column, props) {
                if (typeof column.isValueEmpty === "function") {
                    return column.isValueEmpty(props);
                }

                const val = this.displayValue(column, props);
                return val == null || val === "";
            },
            refresh() {
                this._performQuery();
            },
            async reloadItems() {
                await this._performQuery();
                // Reset to first page when items changes. Items may have
                // been filtered externally - for instance, by a checkbox
                // independent of the pc-table - and we don't want to leave the
                // user on a page that now doesn't exist. However, if items
                // is provided as a function, assume that the table consumer
                // won't be doing this kind of external filtering
                if (!this.preservePage && typeof this.items !== "function") {
                    this.currentPage = 1;
                } else if (this.currentPage > this.numberOfPages) {
                    this.currentPage = this.numberOfPages;
                }
            },
            rehoistGlobalScroll() {
                if (this.useTableView && this.useGlobalScroll) {
                    if (this.$refs.tableView) {
                        this.$refs.tableView.hoistToGlobalScroll();
                    } else {
                        this.$app.hoistElementToGlobalScroll(null);
                    }
                }
            },
            helpClick(index) {
                if (this.internalColumns[index].showHelp) {
                    const cloneColumn = { ...this.internalColumns[index] };
                    this.$emit("help-clicked", cloneColumn);
                }
            },
            rowClick(itemIndex) {
                if (this.enableContextMenu && this.useTableView) {
                    return;
                }

                if (this.currentPageItems.length <= itemIndex) {
                    return;
                }

                // Do not expand/collapse the row if the user is just selecting text
                if (window && window.getSelection().toString()) {
                    return;
                }

                // Click is valid if item has actions, or if clicking a card
                // that has more columns than fit on the collapsed card.
                const item = this.currentPageItems[itemIndex];

                // Do not expand/collapse the row if:
                //   The row is intended to not be expanded by showActions (defaults to false if not provided), and
                //   either the table is in table view, or is in card view and has equal or fewer columns than the cutoff
                if (
                    !this.showActions(item.item) &&
                    (this.useTableView || this.visibleColumns.length <= this.cardPropertyCutoff + 1)
                ) {
                    return;
                }

                // Don't edit the expanded value if it is overriden
                if (typeof this.expandedOverride === "function") {
                    this.$emit("row-clicked", item.item);
                    return;
                }

                const oldExpanded = item.expanded;

                this.currentPageItems.forEach((i) => (i.expanded = false));
                item.expanded = !oldExpanded;

                const rowItem = this.currentPageItems[itemIndex];
                if (item.expanded) {
                    this.$emit("row-expanded", rowItem.item);
                } else {
                    this.$emit("row-collapsed", rowItem.item);
                }
            },
            sortClick(index, event) {
                const column = this.internalColumns[index];
                if (column.orderable) {
                    const sortIndex = this.colIndexSortOrder.findIndex((i) => i === index);
                    if (event && event.ctrlKey) {
                        if (sortIndex === -1) {
                            // column not currently in sort order, so add it
                            column.sortingDir = "asc"; //always start ascending
                            this.colIndexSortOrder.push(index);
                        } else {
                            // cycle the current column
                            if (column.sortingDir === "asc") {
                                column.sortingDir = "desc";
                            } else {
                                // clear the sorting and remove from order
                                if (this.colIndexSortOrder.length === 1) {
                                    // unless this is the only column
                                    column.sortingDir = "asc";
                                } else {
                                    column.sortingDir = "";
                                    this.colIndexSortOrder.splice(sortIndex, 1);
                                }
                            }
                        }
                    } else {
                        let newSortDir = "asc";
                        if (sortIndex !== -1) {
                            newSortDir = column.sortingDir === "asc" ? "desc" : "asc";
                        }
                        this.colIndexSortOrder = [];
                        this.internalColumns.forEach((ic) => {
                            ic.sortingDir = "";
                        });
                        column.sortingDir = newSortDir;
                        this.colIndexSortOrder.push(index);
                    }
                    this.currentPage = 1;
                }
            },
            cellClick(colIndex, item) {
                this.$emit("cell-click", colIndex, item);
            },
            search(searchTerm) {
                this.currentPage = 1;
                this.searchTerm = searchTerm;
            },
            searchWithFilters(searchTerm, searchFilters) {
                this.searchFilters = searchFilters;
                this.search(searchTerm);
            },
            pageChanged(page) {
                this.currentPage = page;
                this.$emit("page-changed", page);
            },
            collapseAll() {
                // Don't edit the expanded value if it is overriden
                if (typeof this.expandedOverride === "function") {
                    return;
                }
                this.currentPageItems.forEach((item) => {
                    const wasExpanded = item.expanded;
                    item.expanded = false;
                    if (wasExpanded) {
                        this.$emit("row-collapsed", item.item);
                    }
                });
            },
            updateCurrentlyExpandedItem(update) {
                const expandedItems = this.currentPageItems.filter((i) => i.expanded);
                if (expandedItems.length === 1) {
                    const row = expandedItems[0];
                    for (const prop in update) {
                        if (update.hasOwnProperty(prop) && row.item.hasOwnProperty(prop)) {
                            row.item[prop] = update[prop];
                        }
                    }
                }
            },
            async openToFirstMatch(filter) {
                this.filterFallbackPage = this.currentPage;
                const onFirstPage = this.currentPage === 1;

                this.currentPage = 1;
                this.filterToFind = filter;

                // If already on first page, need to manually perform query. Otherwise
                // this.query() gets re-evaluated when currentPage is set to 1 and
                // query automatically performed.
                // Also don't initiate new query in the middle of existing one
                if (onFirstPage && !this.loading) {
                    await this.$nextTick();
                    this._performQuery();
                }
            },
            _buildInternalColumns() {
                const cols = this.columns.map((c) => ({
                    header: c.header || c.property,
                    property: c.property,
                    orderable: c.orderable || false,
                    searchable: c.searchable || false,
                    visible: c.visible || false,
                    sticky: c.sticky || false,
                    sortingDir: "",
                    isCardHeader: c.isCardHeader || false,
                    columnClass: c.columnClass || "",
                    headerClass: c.headerClass || "",
                    displayValue: c.displayValue,
                    isValueEmpty: c.isValueEmpty,
                    showRequired: c.showRequired || false,
                    showHelp: c.showHelp || false
                }));
                //sticky columns must start at 0 and be contiguous
                let usingSticky = cols.length > 0 && cols[0].sticky;
                cols.forEach((c) => {
                    c.sticky = c.sticky && usingSticky;
                    usingSticky = c.sticky;
                });
                if (cols.length > 0) {
                    this.initialSort.forEach((colSort) => {
                        cols[colSort[0]].sortingDir = colSort[1];
                    });
                }
                return {
                    cols: cols,
                    indexSortOrder: this.initialSort.map((cs) => cs[0])
                };
            },
            async _performQuery() {
                this._updateLocalStorage();

                if (typeof this.items === "function") {
                    this.loading = true;
                    // clone the query to make sure its not mutated
                    const queryClone = JSON.parse(JSON.stringify(this.query));
                    try {
                        const result = await this.items(queryClone);
                        this._processQueryResponse(result);
                    } catch {
                        // if the query failed and there was a search term, clean out the search term as that MAY have been the cause of the failure
                        if (this.searchTerm) {
                            this.searchTerm = ""; // won't update the UI as binding is nonreactive
                            this._updateLocalStorage();
                            // to not lie, because else the screen shows the search term but the old list of projects
                            this.$app.reloadWindow();
                        }
                    } finally {
                        this.loading = false;
                    }
                } else {
                    //build a proxy index array to enable stable sorts.
                    let indexArray = this.items.map((item, index) => index);

                    //first filter
                    if (this.searchTerm) {
                        this._searchMatcher = new RegExp(escapeRegex(this.searchTerm), "i");
                    }
                    indexArray = indexArray.filter(this._matchesSearch);
                    const filteredCount = indexArray.length;

                    //then sort
                    indexArray.sort(this._compareByIndex);

                    this.allResultsIndexArray = indexArray;

                    //then paginate
                    indexArray = indexArray.slice(this.query.start, this.query.start + this.query.length);

                    const response = {
                        recordsTotal: this.items.length,
                        recordsFiltered: filteredCount,
                        data: indexArray.map((i) => this.items[i])
                    };
                    this._processQueryResponse(response);
                }
            },
            getAllSearchResultItemsClientSideOnly() {
                if (typeof this.items === "function") {
                    return [];
                }

                return deepClone(this.allResultsIndexArray.map((i) => this.items[i]));
            },
            _matchesSearch(itemIndex) {
                const item = this.items[itemIndex];
                if (this.searchTerm && this._searchMatcher) {
                    for (let i = 0; i < this.columns.length; i++) {
                        const col = this.columns[i];
                        if (col.searchable) {
                            const searchValue = this._getSearchValue(item, col);
                            const matches = this._searchMatcher.test(searchValue);
                            if (matches) {
                                return true;
                            }
                        }
                    }
                    return false;
                }
                return true;
            },
            _getSearchValue(item, column) {
                if (typeof column.searchValue === "function") {
                    return column.searchValue(item);
                }
                let prop = column.property;
                if (typeof column.searchValue === "string") {
                    prop = column.searchValue;
                }

                const value = item[prop];
                return value == null ? "" : value.toString().toLowerCase();
            },
            _processQueryResponse(response) {
                this.totalItemCount = response.recordsTotal;
                this.filteredItemCount = response.recordsFiltered;
                if (this.filterToFind) {
                    const filterToFind = this.filterToFind;
                    const foundIndex = response.data.findIndex((item) => filterToFind(item));
                    if (foundIndex >= 0) {
                        this.filterToFind = null;
                        this.filterFallbackPage = null;
                        this.currentPageItems = response.data.map((i, index) => ({
                            expanded:
                                typeof this.expandedOverride === "function"
                                    ? this.expandedOverride(i)
                                    : index === foundIndex,
                            item: i,
                            id: index === foundIndex ? "initialScrollElement" : undefined
                        }));
                        this.$nextTick().then(() => {
                            scrollToElement("initialScrollElement");
                        });
                    } else {
                        this._continueFilterSearch();
                    }
                } else {
                    this.currentPageItems = response.data.map((i) => ({
                        expanded: typeof this.expandedOverride === "function" ? this.expandedOverride(i) : false,
                        item: i
                    }));
                }
            },
            _continueFilterSearch() {
                this.currentPage++;
                if (this.query.start >= this.filteredItemCount) {
                    // We've searched all items. Go back to fallback page and cancel search
                    this.currentPage = this.filterFallbackPage;
                    this.filterToFind = null;
                    this.filterFallbackPage = null;
                }
            },
            _compareByIndex(indexA, indexB) {
                const itemA = this.items[indexA];
                const itemB = this.items[indexB];

                for (let i = 0; i < this.query.order.length; i++) {
                    const order = this.query.order[i];
                    const col = this.columns[order.column];

                    const propA = this._getSortingValue(itemA, col);
                    const propB = this._getSortingValue(itemB, col);

                    const compare = this._compare(propA, propB);
                    if (compare !== 0) {
                        return order.dir === "asc" ? compare : -compare;
                    }
                }

                // to make sort stable, order by index if everything else is the same
                return this._compare(indexA, indexB);
            },
            _compare(propA, propB) {
                if (propA < propB) {
                    return -1;
                }
                if (propA > propB) {
                    return 1;
                }
                return 0;
            },
            _getSortingValue(item, column) {
                if (typeof column.sortValue === "function") {
                    return column.sortValue(item);
                }
                let prop = column.property;
                if (typeof column.sortValue === "string") {
                    prop = column.sortValue;
                }
                const value = item[prop];
                if (typeof value === "number") {
                    return value;
                }
                return value == null ? "" : value.toString().toLowerCase();
            },
            _getItemsPerPageLocalStorageKey() {
                return this._getLocalStorageKey("_itemsPerPage");
            },
            _getLocalStorageKey(extension = "") {
                const userId = this.$app.LoggedInUser.PublicId;
                const path = window.location.pathname + this.$app.ProgramId;
                return `pc-table_${this.stateKey}_${path}_${userId}${extension}`;
            },
            _setLocalStorage() {
                const value = {
                    currentPage: this.currentPage,
                    itemsPerPage: this.internalItemsPerPage,
                    searchTerm: this.searchTerm,
                    searchFilters: this.searchFilters,
                    colIndexSortOrder: this.colIndexSortOrder,
                    internalColumnsDirs: this.internalColumns.map((cols) => cols.sortingDir)
                };
                safeSetItem(this._getLocalStorageKey(), JSON.stringify(value));
            },
            _updateLocalStorage() {
                if (this.saveState && this.stateKey) {
                    this._setLocalStorage();
                }
            }
        }
    };
</script>
